import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Alert,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { addDoc, collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { alpha, useTheme } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import LogFormShell from '../../../components/UI/LogFormShell';
import { buildMedicationDoseInstancesForDate, normalizeMedicationDetail } from '../../../components/Dashboard/shared/childMedicationHelpers';
import { auth, db } from '../../../services/firebase';
import { fetchMedications } from '../../../services/medicationService';
import colors from '../../../assets/theme/colors';
import { LOG_TYPES } from '../../../constants/logTypeRegistry';
import { CARE_SNAPSHOT_STEPS } from '../../../constants/careSnapshotSections';
import LogTimeField from '../../../components/UI/LogTimeField';
import MedicationBackfillDialog from './MedicationBackfillDialog';
import MedicationDuplicateWarningDialog from './MedicationDuplicateWarningDialog';
import {
  detectPossibleMedicationDuplicate,
  getMedicationLogDateKey,
  normalizeMedicationLogRecord,
} from '../../../services/medicationLogDuplicateService';
import { getAdaptiveMedicationLogConfig } from '../../../components/Dashboard/shared/adaptiveMedicationLogConfig';

const todayKey = getMedicationLogDateKey(new Date());
const getMedicationTakenStorageKey = (childId) => `captureez:medication-log:${childId || 'unknown'}:${todayKey}`;
const medicationTakenMemoryCache = typeof window !== 'undefined'
  ? (window.__captureezMedicationTakenCache || (window.__captureezMedicationTakenCache = {}))
  : {};

const getCachedTakenEvents = (childId) => {
  const cacheKey = getMedicationTakenStorageKey(childId);
  const memoryValue = medicationTakenMemoryCache[cacheKey];
  if (memoryValue && typeof memoryValue === 'object') {
    return memoryValue;
  }

  const storedValue = readStoredTakenEvents(childId);
  medicationTakenMemoryCache[cacheKey] = storedValue;
  return storedValue;
};

const setCachedTakenEvents = (childId, nextMap) => {
  const cacheKey = getMedicationTakenStorageKey(childId);
  medicationTakenMemoryCache[cacheKey] = { ...(nextMap || {}) };
  writeStoredTakenEvents(childId, medicationTakenMemoryCache[cacheKey]);
};

const readStoredTakenEvents = (childId) => {
  if (typeof window === 'undefined' || !childId) {
    return {};
  }

  try {
    const cacheKey = getMedicationTakenStorageKey(childId);
    const raw = window.localStorage.getItem(cacheKey) || window.sessionStorage.getItem(cacheKey);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeStoredTakenEvents = (childId, nextMap) => {
  if (typeof window === 'undefined' || !childId) {
    return;
  }

  try {
    const safeMap = Object.keys(nextMap || {}).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    const serialized = JSON.stringify(safeMap);
    const cacheKey = getMedicationTakenStorageKey(childId);
    window.localStorage.setItem(cacheKey, serialized);
    window.sessionStorage.setItem(cacheKey, serialized);
  } catch (error) {
    // Ignore storage failures; Firestore writes remain the source of record.
  }
};

const parseTimeToMinutes = (timeValue) => {
  if (!timeValue) {
    return null;
  }

  const [hours, minutes] = timeValue.split(':').map((value) => Number(value));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
};

const formatTimeLabel = (timeValue) => {
  if (!timeValue) {
    return 'No scheduled time';
  }

  const [hours, minutes] = timeValue.split(':').map((value) => Number(value));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return timeValue;
  }

  const preview = new Date();
  preview.setHours(hours, minutes, 0, 0);
  return preview.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatPlannedDoseLabel = (dose, unit) => {
  const parts = [dose, unit].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Dose not set';
};

const formatScheduleStatus = (event) => {
  if (event.state === 'taken') {
    return 'Completed';
  }

  if (event.scheduleType === 'as_needed') {
    return 'As needed';
  }

  if (event.scheduleType === 'occasionally') {
    return 'Occasional';
  }

  if (event.scheduleType === 'short_course' && event.courseDayLabel) {
    return event.courseDayLabel;
  }

  if (event.scheduleType === 'every_x_hours') {
    return event.intervalHours ? `Every ${event.intervalHours} hours` : 'Interval';
  }

  if (!event.time) {
    return 'As needed';
  }

  if (event.dueStatus === 'overdue') {
    return 'Overdue';
  }

  if (event.dueStatus === 'upcoming') {
    return 'Upcoming';
  }

  return 'Due now';
};

const getMedicationDoseLifecycle = (event = {}, loggedEvent = null) => {
  const isTaken = Boolean(loggedEvent || event.state === 'taken');

  if (isTaken) {
    return {
      state: 'taken',
      canMarkTaken: false,
      showPrimaryAction: false,
      showSecondaryAction: true,
      scheduleStatus: 'Completed',
    };
  }

  return {
    state: 'due',
    canMarkTaken: true,
    showPrimaryAction: true,
    showSecondaryAction: true,
    scheduleStatus: formatScheduleStatus(event),
  };
};

const getMedicationEventGroupKey = (event = {}) => {
  if (event.scheduleType === 'as_needed' || event.scheduleType === 'occasionally') {
    return 'as_needed';
  }

  if (event.scheduleType === 'short_course') {
    return 'short_course';
  }

  if (event.scheduleType === 'every_x_hours') {
    return 'interval';
  }

  return 'routine';
};

const medicationEventGroups = [
  { key: 'routine', label: 'Scheduled today' },
  { key: 'interval', label: 'Every X hours' },
  { key: 'as_needed', label: 'As needed & occasional' },
  { key: 'short_course', label: 'Short course' },
];

const getMedicationEventSupportText = (event = {}, medicationConfig) => {
  if (event.scheduleType === 'as_needed') {
    return [event.asNeededReason, event.maxFrequency].filter(Boolean).join(' · ') || 'Log when given.';
  }

  if (event.scheduleType === 'occasionally') {
    return event.purpose || event.notes || 'Log when given.';
  }

  if (event.scheduleType === 'every_x_hours') {
    return [
      event.intervalHours ? `Repeat every ${event.intervalHours} hours` : '',
      event.intervalStartTime ? `starting ${formatTimeLabel(event.intervalStartTime)}` : '',
    ].filter(Boolean).join(' · ') || 'Interval medication.';
  }

  if (event.scheduleType === 'short_course') {
    return [event.courseDayLabel, event.time ? formatTimeLabel(event.time) : ''].filter(Boolean).join(' · ') || 'Short course medication.';
  }

  if (event.time) {
    if (event.dueStatus === 'overdue') return medicationConfig.pendingOverdueLabel;
    if (event.dueStatus === 'upcoming') return medicationConfig.pendingUpcomingLabel;
    return medicationConfig.pendingDueNowLabel;
  }

  return medicationConfig.pendingAsNeededLabel;
};

const shouldAskMedicationEffect = (event = {}) => (
  ['as_needed', 'occasionally', 'every_x_hours'].includes(String(event.scheduleType || '').trim())
  || String(event.medicationCategory || event.category || '').trim() === 'prn'
);

const getMedicationEffectLabel = (value = '', medicationConfig = {}) => (
  (medicationConfig.effectOptions || []).find((option) => option.value === value)?.label || ''
);

const getEntryContextText = (entry = {}) => String(
  entry.note
  || entry.notes
  || entry.sourceText
  || entry.originalData?.sourceText
  || entry.source?.body
  || entry.originalData?.source?.body
  || entry.content
  || entry.originalData?.content
  || entry.text
  || entry.originalData?.text
  || entry.primaryText
  || entry.originalData?.primaryText
  || entry.label
  || entry.originalData?.label
  || entry.titlePrefix
  || entry.originalData?.titlePrefix
  || entry.title
  || entry.originalData?.title
  || ''
).trim();

const splitMedicationDisplayName = (name = '') => {
  const raw = String(name || '').trim();
  if (!raw) {
    return { primary: '', secondary: '' };
  }

  if (raw.includes('(') && raw.includes(')')) {
    const primary = raw.replace(/\s*\(([^)]+)\)\s*$/, '').trim();
    const secondary = raw.match(/\(([^)]+)\)\s*$/)?.[1]?.trim() || '';
    return { primary: primary || raw, secondary };
  }

  const [primary, ...rest] = raw.split('·');
  return {
    primary: primary.trim(),
    secondary: rest.join('·').trim(),
  };
};

const getDoseLabel = (event = {}) => {
  const amount = [event.plannedDose || event.dose, event.unit].filter(Boolean).join(' ').trim();
  return amount || 'Dose not set';
};

const parseDoseValue = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '';
  }

  const numberMatch = raw.match(/(\d+(?:\.\d+)?)/);
  return numberMatch?.[1] || raw;
};

const parseDoseNumber = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const buildTodayTimestampFromTime = (timeValue) => {
  const now = new Date();
  if (!timeValue) {
    return now;
  }

  const [hours, minutes] = String(timeValue).split(':').map((value) => Number(value));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return now;
  }

  const next = new Date(now);
  next.setHours(hours, minutes, 0, 0);
  return next;
};

const medicationSheetColors = {
  ink: colors.semantic.ink,
  ink2: colors.semantic.ink2,
  ink3: colors.semantic.ink3,
  line: colors.semantic.line,
  meds: colors.semantic.meds,
  sageDeep: colors.semantic.sageDeep,
  sageSoft: colors.semantic.sageSoft,
  sageMist: colors.semantic.sageMist,
};

const ProgressRing = ({ taken = 0, total = 0 }) => {
  const size = 44;
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = total > 0 ? Math.min(1, Math.max(0, taken / total)) : 0;
  const dashOffset = circumference * (1 - ratio);

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <Box
        component="svg"
        viewBox={`0 0 ${size} ${size}`}
        sx={{ width: size, height: size, display: 'block' }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(47, 90, 73, 0.15)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={medicationSheetColors.meds.ink}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: medicationSheetColors.meds.ink,
          fontSize: '12px',
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {taken}/{total}
      </Box>
    </Box>
  );
};

const buildEventKey = (medicationId, scheduleId, scheduleIndex, scheduleTime) => (
  [medicationId, scheduleId || scheduleIndex, scheduleTime || 'anytime'].join(':')
);

const parseLogTimeValue = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatLoggedTimeLabel = (value) => {
  const parsed = parseLogTimeValue(value);
  if (!parsed) {
    return '';
  }

  return parsed.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const normalizeScheduleTime = (value) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  const parts = raw.split(':');
  if (parts.length < 2) {
    return raw;
  }

  const hours = String(Number(parts[0])).padStart(2, '0');
  const minutes = String(Number(parts[1])).padStart(2, '0');
  if (hours === 'NaN' || minutes === 'NaN') {
    return raw;
  }

  return `${hours}:${minutes}`;
};

const getMedicationLogInstanceKey = (instance = {}) => buildEventKey(
  instance.medicationId,
  instance.scheduleId,
  instance.scheduleIndex,
  instance.scheduledTime
);

const getMedicationLogRecordKey = (record = {}) => (
  record.medicationScheduleKey
  || buildEventKey(
    record.medicationId || record.medId,
    record.medicationScheduleId,
    record.medicationScheduleIndex,
    record.medicationScheduleTime || record.time
  )
);

const getMedicationLogSortValue = (record = {}) => {
  const parsed = parseLogTimeValue(
    record.actualDateTime
    || record.takenAt
    || record.timestampUtc
    || record.timestamp
    || record.createdAt
  );
  return parsed ? parsed.getTime() : 0;
};

const medicationLogMatchesInstance = (record = {}, instance = {}) => {
  if (!record || !instance) {
    return false;
  }

  const recordDateKey = record.dateKey || record.entryDate || '';
  if (recordDateKey && instance.scheduledDateKey && recordDateKey !== instance.scheduledDateKey) {
    return false;
  }

  const recordMedicationId = String(record.medicationId || record.medId || '').trim();
  const instanceMedicationId = String(instance.medicationId || '').trim();
  if (recordMedicationId && instanceMedicationId && recordMedicationId !== instanceMedicationId) {
    return false;
  }

  const recordKey = getMedicationLogRecordKey(record);
  const instanceKey = getMedicationLogInstanceKey(instance);
  if (recordKey && instanceKey && recordKey === instanceKey) {
    return true;
  }

  const recordScheduleId = String(record.medicationScheduleId || '').trim();
  const instanceScheduleId = String(instance.scheduleId || '').trim();
  if (recordScheduleId && instanceScheduleId && recordScheduleId === instanceScheduleId) {
    return true;
  }

  const recordScheduleTime = normalizeScheduleTime(record.medicationScheduleTime || record.time || '');
  const instanceScheduleTime = normalizeScheduleTime(instance.scheduledTime || '');
  if (recordScheduleTime && instanceScheduleTime && recordScheduleTime === instanceScheduleTime) {
    return true;
  }

  if (!instanceScheduleTime && !recordScheduleTime) {
    return recordMedicationId === instanceMedicationId;
  }

  return false;
};

const findPersistedMedicationLogForInstance = (instance = {}, logs = []) => {
  const sortedLogs = [...logs].sort((left, right) => getMedicationLogSortValue(right) - getMedicationLogSortValue(left));
  return sortedLogs.find((record) => (
    record.category === 'medication'
    && record.status !== 'deleted'
    && medicationLogMatchesInstance(record, instance)
  )) || null;
};

const MedicationRow = ({
  event,
  medicationConfig,
  onMarkTaken,
  onLogTime,
  onUndo,
  onOpenDoseEditor,
  onCancelDoseEditor,
  onConfirmDoseEditor,
  onDoseEditorChange,
  doseEditor,
  confirmButtonRef,
  savingKey,
  canMarkTaken,
  recentlyMarkedKey,
}) => {
  const theme = useTheme();
  const meds = theme.palette.semantic?.meds || medicationSheetColors.meds;
  const line = theme.palette.semantic?.line || medicationSheetColors.line;
  const ink = theme.palette.semantic?.ink || medicationSheetColors.ink;
  const ink2 = theme.palette.semantic?.ink2 || medicationSheetColors.ink2;
  const ink3 = theme.palette.semantic?.ink3 || medicationSheetColors.ink3;
  const lifecycle = getMedicationDoseLifecycle(event, event.loggedEvent);
  const isTaken = lifecycle.state === 'taken';
  const isSaving = savingKey === event.eventKey;
  const isPulse = recentlyMarkedKey === event.eventKey;
  const isEditing = doseEditor?.eventKey === event.eventKey;
  const scheduleLabel = event.scheduleType === 'as_needed'
    ? 'As needed'
    : event.scheduleType === 'occasionally'
      ? 'Occasional'
      : event.scheduleType === 'every_x_hours'
        ? (event.intervalHours ? `Every ${event.intervalHours} hours` : 'Interval')
        : event.scheduleType === 'short_course'
          ? (event.courseDayLabel || 'Short course')
          : (event.scheduleTimeLabel || formatTimeLabel(event.scheduledTime));
  const plannedDoseLabel = formatPlannedDoseLabel(event.plannedDose || event.dose, event.unit);
  const loggedDoseLabel = formatPlannedDoseLabel(
    event.loggedEvent?.actualDose ?? event.loggedEvent?.givenDose ?? event.loggedEvent?.dose,
    event.loggedEvent?.unit || event.unit
  );
  const scheduleStatusLabel = isTaken ? medicationConfig.completedStatusLabel : lifecycle.scheduleStatus;
  const medicationName = splitMedicationDisplayName(event.medicationName);
  const IconComponent = LOG_TYPES.medication.iconComponent;
  const actualTimeLabel = event.loggedEvent?.takenAt
    ? formatLoggedTimeLabel(event.loggedEvent.takenAt)
    : formatLoggedTimeLabel(event.loggedEvent?.actualDateTime)
      || event.loggedEvent?.timeLabel
      || '';
  const actualDoseDiffers = Boolean(
    isTaken
    && event.loggedEvent
    && String(event.loggedEvent?.actualDose ?? event.loggedEvent?.givenDose ?? event.loggedEvent?.dose ?? '').trim()
      !== String(event.plannedDose || event.dose || '').trim()
  );
  const showEffectQuestion = shouldAskMedicationEffect(event);
  const doseEditorPanel = isEditing ? (
    <Box
      sx={{
        mt: 1.15,
        p: 1.25,
        borderRadius: '10px',
        bgcolor: '#FAFCFB',
        border: `1px solid ${line}`,
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
          <TextField
            fullWidth
            type="number"
            value={doseEditor?.dose ?? ''}
            onChange={(inputEvent) => onDoseEditorChange?.(event.eventKey, { dose: inputEvent.target.value })}
            placeholder="10"
            InputProps={{
              endAdornment: <InputAdornment position="end">mg</InputAdornment>,
              inputProps: {
                min: 0,
                step: '0.5',
                inputMode: 'decimal',
                'aria-label': 'dose amount',
              },
            }}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: 54,
                borderRadius: '16px',
                bgcolor: '#FFFFFF',
                fontSize: '17px',
                fontWeight: 600,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: line,
              },
            }}
          />
          <Box sx={{ flex: '1 1 42%' }}>
            <LogTimeField
              value={doseEditor?.time || ''}
              onChange={(inputEvent) => onDoseEditorChange?.(event.eventKey, { time: inputEvent.target.value })}
              label="Time"
              showLabel={false}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: 54,
                  borderRadius: '16px',
                  bgcolor: '#FFFFFF',
                },
                '& input': {
                  paddingTop: '0.95rem',
                  paddingBottom: '0.85rem',
                },
              }}
            />
          </Box>
        </Box>

        <TextField
          fullWidth
          value={doseEditor?.note || ''}
          onChange={(inputEvent) => onDoseEditorChange?.(event.eventKey, { note: inputEvent.target.value })}
            placeholder={medicationConfig.notePlaceholder}
          inputProps={{ maxLength: 120 }}
          sx={{
            '& .MuiInputBase-root': {
              minHeight: 48,
              borderRadius: '16px',
              bgcolor: '#FFFFFF',
              fontSize: '0.94rem',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: line,
            },
          }}
        />

        {showEffectQuestion ? (
          <Box>
            <Typography sx={{ fontSize: '0.78rem', color: ink2, fontWeight: 800, mb: 0.75 }}>
              {medicationConfig.effectQuestionLabel}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 0.65 }}>
              {(medicationConfig.effectOptions || []).map((option) => {
                const isSelected = doseEditor?.effect === option.value;
                return (
                  <Button
                    key={option.value}
                    type="button"
                    variant="outlined"
                    onClick={() => onDoseEditorChange?.(event.eventKey, { effect: isSelected ? '' : option.value })}
                    sx={{
                      minHeight: 38,
                      borderRadius: '999px',
                      px: 1,
                      borderColor: isSelected ? medicationSheetColors.sageDeep : line,
                      bgcolor: isSelected ? 'rgba(111, 165, 148, 0.16)' : '#FFFFFF',
                      color: isSelected ? medicationSheetColors.sageDeep : ink2,
                      fontWeight: 750,
                      fontSize: '0.74rem',
                      lineHeight: 1.1,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: isSelected ? 'rgba(111, 165, 148, 0.18)' : '#FFFFFF',
                        borderColor: medicationSheetColors.sageDeep,
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Box>
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => onCancelDoseEditor?.(event.eventKey)}
            sx={{
              flex: 1,
              height: 40,
              borderRadius: '999px',
              border: `1.5px solid ${line}`,
              color: medicationSheetColors.ink2,
              bgcolor: '#FFFFFF',
              fontWeight: 650,
              fontSize: '0.84rem',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            autoFocus
            ref={confirmButtonRef}
            onClick={() => onConfirmDoseEditor?.(event.eventKey)}
            disabled={isSaving || !doseEditor?.dose}
            sx={{
              flex: 2,
              height: 40,
              borderRadius: '999px',
              bgcolor: medicationSheetColors.sageDeep,
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: '0.88rem',
              textTransform: 'none',
              boxShadow: '0 2px 6px rgba(111,165,148,0.22)',
              '&:hover': {
                bgcolor: colors.semantic.sageDeep,
              },
              '&.Mui-disabled': {
                bgcolor: line,
                color: medicationSheetColors.ink3,
              },
            }}
          >
            {medicationConfig.confirmLabel(doseEditor?.dose)}
          </Button>
        </Box>
      </Stack>
    </Box>
  ) : null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        p: '14px 14px 12px',
        border: `1.5px solid ${isTaken ? meds.ink3 : line}`,
        bgcolor: isTaken ? 'rgba(156, 198, 184, 0.12)' : '#FFFFFF',
        boxShadow: 'none',
        overflow: 'hidden',
        transform: isPulse ? 'scale(1.01)' : 'scale(1)',
        transition: 'transform 220ms ease, background-color 180ms ease, border-color 180ms ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '11px',
            bgcolor: isTaken ? meds.ink : meds.bg,
            color: isTaken ? '#FFFFFF' : meds.ink,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isTaken ? (
            <CheckRoundedIcon sx={{ fontSize: 22, color: '#FFFFFF', strokeWidth: 2.6 }} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {IconComponent ? <IconComponent sx={{ fontSize: 19, color: meds.ink }} /> : null}
            </Box>
          )}
        </Box>

        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, lineHeight: 1.05, color: medicationSheetColors.meds.ink, fontSize: '1.02rem', letterSpacing: '-0.02em' }}>
                {isTaken ? medicationConfig.givenHeading : scheduleLabel}
              </Typography>
              <Typography sx={{ mt: 0.3, fontWeight: 700, lineHeight: 1.12, color: ink, fontSize: '0.98rem', letterSpacing: '-0.01em' }} noWrap>
                {medicationName.primary || event.medicationName}
                {medicationName.secondary ? (
                  <Box component="span" sx={{ color: ink3, fontWeight: 500 }}>
                    {` · ${medicationName.secondary}`}
                  </Box>
                ) : null}
              </Typography>
            </Box>

            <Chip
              label={scheduleStatusLabel}
              size="small"
              sx={{
                height: 24,
                borderRadius: '999px',
                bgcolor: isTaken ? 'rgba(47, 90, 73, 0.14)' : (event.dueStatus === 'overdue' ? 'rgba(217, 121, 121, 0.12)' : meds.bg),
                color: isTaken ? colors.semantic.sageDeep : (event.dueStatus === 'overdue' ? '#B24B4B' : meds.ink),
                fontSize: '0.78rem',
                fontWeight: 800,
                '& .MuiChip-label': {
                  px: 0.85,
                },
              }}
            />
          </Box>

          <Typography sx={{ mt: 0.45, fontSize: '0.84rem', color: ink2, fontWeight: 650, lineHeight: 1.25 }}>
            {`${medicationConfig.plannedDoseLabel}: ${plannedDoseLabel}`}
          </Typography>

          {isTaken ? (
            <Box sx={{ mt: 0.6, display: 'flex', flexDirection: 'column', gap: 0.25, p: 0.9, borderRadius: '12px', bgcolor: 'rgba(47, 90, 73, 0.06)' }}>
              <Typography sx={{ fontSize: '0.82rem', color: colors.semantic.sageDeep, fontWeight: 800, lineHeight: 1.2 }}>
                Logged at {actualTimeLabel || 'recently'}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: ink2, fontWeight: 700, lineHeight: 1.2 }}>
                {medicationConfig.givenDoseLabel}: {loggedDoseLabel}
              </Typography>
              {actualDoseDiffers ? (
                <Typography sx={{ fontSize: '0.82rem', color: ink2, fontWeight: 700, lineHeight: 1.2 }}>
                  {medicationConfig.plannedDoseLabel}: {plannedDoseLabel}
                </Typography>
              ) : null}
              {event.loggedEvent?.medicationEffectLabel ? (
                <Typography sx={{ fontSize: '0.82rem', color: ink2, fontWeight: 700, lineHeight: 1.2 }}>
                  {medicationConfig.effectQuestionLabel}: {event.loggedEvent.medicationEffectLabel}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <Typography sx={{ mt: 0.45, fontSize: '0.82rem', color: ink3, fontWeight: 600, lineHeight: 1.2 }}>
              {getMedicationEventSupportText(event, medicationConfig)}
            </Typography>
          )}
        </Box>
      </Box>

      {lifecycle.showPrimaryAction ? (
        <>
          <Divider sx={{ my: 1.1, borderColor: meds.soft }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
            <Button
              type="button"
              variant="contained"
              disabled={isSaving || !canMarkTaken || !lifecycle.canMarkTaken}
              onClick={() => onMarkTaken?.(event)}
              startIcon={<CheckRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                flex: '1 1 auto',
                height: 40,
                borderRadius: '999px',
                bgcolor: medicationSheetColors.sageDeep,
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.88rem',
                boxShadow: '0 2px 6px rgba(111,165,148,0.25)',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: medicationSheetColors.sageDeep,
                  boxShadow: '0 4px 10px rgba(111,165,148,0.28)',
                },
                '&.Mui-disabled': {
                  bgcolor: line,
                  color: ink3,
                },
              }}
            >
              {isSaving ? medicationConfig.primaryActionLoadingLabel : medicationConfig.primaryActionLabel}
            </Button>
            {lifecycle.showSecondaryAction && onLogTime ? (
              <Button
                type="button"
                variant="outlined"
                disabled={isSaving || !canMarkTaken || !lifecycle.canMarkTaken || isEditing}
                onClick={() => onLogTime(event)}
                startIcon={<AccessTimeRoundedIcon sx={{ fontSize: 18 }} />}
                sx={{
                  flex: '0 0 auto',
                  height: 40,
                  borderRadius: '999px',
                  bgcolor: '#FFFFFF',
                  border: `1.5px solid ${line}`,
                  color: ink2,
                  fontWeight: 650,
                  fontSize: '0.82rem',
                  px: 1.75,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#FFFFFF',
                    borderColor: meds.soft,
                  },
                  '&.Mui-disabled': {
                    color: ink3,
                    borderColor: line,
                  },
                }}
              >
                {medicationConfig.timeActionLabel}
              </Button>
            ) : null}
          </Box>
          {doseEditorPanel}
        </>
      ) : (
        <>
          <Divider sx={{ my: 1.1, borderColor: meds.soft }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, minWidth: 0 }}>
                <CheckRoundedIcon sx={{ fontSize: 18, color: colors.semantic.sageDeep }} />
                <Typography sx={{ color: colors.semantic.sageDeep, fontWeight: 650, fontSize: '0.82rem', lineHeight: 1.2 }}>
                  {medicationConfig.givenHeading}
                </Typography>
              </Box>
              <Typography sx={{ color: ink2, fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.2 }}>
                Logged at {actualTimeLabel || 'recently'}
              </Typography>
              <Typography sx={{ color: ink2, fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.2 }}>
                {medicationConfig.givenDoseLabel}: {loggedDoseLabel}
              </Typography>
              {actualDoseDiffers ? (
                <Typography sx={{ color: ink2, fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.2 }}>
                  {medicationConfig.plannedDoseLabel}: {plannedDoseLabel}
                </Typography>
              ) : null}
              {event.loggedEvent?.medicationEffectLabel ? (
                <Typography sx={{ color: ink2, fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.2 }}>
                  {medicationConfig.effectQuestionLabel}: {event.loggedEvent.medicationEffectLabel}
                </Typography>
              ) : null}
            </Box>
            {onUndo ? (
              <Button
                type="button"
                variant="text"
                onClick={() => onUndo(event)}
                sx={{
                  minWidth: 0,
                  px: 0,
                  py: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: ink2,
                  lineHeight: 1,
                  '&:hover': {
                    bgcolor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                Undo log
              </Button>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.9 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => onOpenDoseEditor?.(event)}
              sx={{
                minWidth: 0,
                height: 34,
                px: 1.35,
                borderRadius: '999px',
                border: `1.5px solid ${line}`,
                color: ink2,
                bgcolor: '#FFFFFF',
                fontWeight: 650,
                fontSize: '0.8rem',
                textTransform: 'none',
              }}
            >
              Edit dose
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={() => onOpenDoseEditor?.(event)}
              sx={{
                minWidth: 0,
                height: 34,
                px: 1.35,
                borderRadius: '999px',
                border: `1.5px solid ${line}`,
                color: ink2,
                bgcolor: '#FFFFFF',
                fontWeight: 650,
                fontSize: '0.8rem',
                textTransform: 'none',
              }}
            >
              Edit time
            </Button>
          </Box>
          {doseEditorPanel}
        </>
      )}
    </Paper>
  );
};

const BulkMedicationLogDialog = ({
  open,
  childId,
  childName,
  child = null,
  medications,
  onClose,
  onSaved,
  user,
  editEntry = null,
}) => {
  const navigate = useNavigate();
  const [authUser] = useAuthState(auth);
  const [loading, setLoading] = useState(false);
  const [medicationLoading, setMedicationLoading] = useState(false);
  const [resolvedMedications, setResolvedMedications] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const [loggedEvents, setLoggedEvents] = useState({});
  const [allMedicationLogs, setAllMedicationLogs] = useState([]);
  const [takenKeys, setTakenKeys] = useState(() => getCachedTakenEvents(childId));
  const [showBackfillDialog, setShowBackfillDialog] = useState(false);
  const [timeEntryPreset, setTimeEntryPreset] = useState(null);
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  const [recentlyMarkedKey, setRecentlyMarkedKey] = useState(null);
  const [toastState, setToastState] = useState(null);
  const [doseEditor, setDoseEditor] = useState(null);
  const [editingLoggedEntry, setEditingLoggedEntry] = useState(null);
  const confirmButtonRef = useRef(null);
  const activeUser = user || authUser;
  const medicationConfig = useMemo(() => getAdaptiveMedicationLogConfig(child), [child]);
  const providedMedications = useMemo(() => (
    Array.isArray(medications) ? medications : []
  ), [medications]);

  const handleManageMedications = () => {
    if (onClose) {
      onClose();
    }
    navigate(`/children/${childId}/edit`, {
      state: {
        childId,
        initialStep: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
        openChildMedicationManager: {
          childId,
        },
      },
    });
  };

  const handleOpenBackfillDialog = () => {
    setTimeEntryPreset(null);
    setShowBackfillDialog(true);
  };

  const handleCloseBackfillDialog = () => {
    setShowBackfillDialog(false);
    setTimeEntryPreset(null);
  };

  const handleOpenTimeEntry = (event) => {
    setTimeEntryPreset({
      medicationId: event.medicationId,
      scheduleId: event.scheduleId,
      status: 'active',
      initialDate: new Date(),
      initialTime: new Date().toTimeString().slice(0, 5),
    });
    setShowBackfillDialog(true);
  };

  const handleOpenDoseEditor = (event) => {
    setEditingLoggedEntry(event.loggedEvent?.id ? event.loggedEvent : null);
    setDoseEditor({
      eventKey: event.eventKey,
      dose: parseDoseValue(
        event.loggedEvent?.actualDose
        ?? event.loggedEvent?.givenDose
        ?? event.plannedDose
        ?? event.dose
        ?? event.medicationScheduleDose
        ?? ''
      ),
      time: event.loggedEvent?.takenAt
        ? new Date(event.loggedEvent.takenAt).toTimeString().slice(0, 5)
        : (event.time || new Date().toTimeString().slice(0, 5)),
      note: event.loggedEvent?.note || '',
      effect: event.loggedEvent?.medicationEffect || '',
    });
  };

  const handleCancelDoseEditor = (eventKey) => {
    setDoseEditor((current) => (current?.eventKey === eventKey ? null : current));
  };

  const handleDoseEditorChange = (eventKey, patch) => {
    setDoseEditor((current) => {
      if (!current || current.eventKey !== eventKey) {
        return current;
      }

      return {
        ...current,
        ...patch,
      };
    });
  };

  const closeDuplicatePrompt = () => {
    setDuplicatePrompt(null);
  };

  const openDuplicatePrompt = ({ reason, medicationName, existingTimeLabel, onConfirm }) => {
    setDuplicatePrompt({
      reason,
      medicationName,
      existingTimeLabel,
      onConfirm,
    });
  };

  useEffect(() => {
    let active = true;

    const loadActiveMedications = async () => {
      if (!open || !childId) {
        setResolvedMedications([]);
        return;
      }

      if (providedMedications.length > 0) {
        setResolvedMedications(providedMedications);
        return;
      }

      setMedicationLoading(true);
      try {
        const fetched = await fetchMedications(childId, false);
        if (active) {
          setResolvedMedications(Array.isArray(fetched) ? fetched : []);
        }
      } catch (error) {
        console.error('Error loading active medications:', error);
        if (active) {
          setResolvedMedications([]);
        }
      } finally {
        if (active) {
          setMedicationLoading(false);
        }
      }
    };

    loadActiveMedications();
    return () => {
      active = false;
    };
  }, [open, childId, providedMedications]);


  useEffect(() => {
    let active = true;

    const loadLoggedEvents = async () => {
      if (!open || !childId) {
        setLoggedEvents({});
        setAllMedicationLogs([]);
        setTakenKeys({});
        return;
      }

      setTakenKeys(getCachedTakenEvents(childId));

      setLoading(true);
      try {
        const q = query(
          collection(db, 'dailyLogs'),
          where('childId', '==', childId),
        );
        const snapshot = await getDocs(q);
        const nextLogs = [];
        const todayMap = {};

        snapshot.forEach((logDoc) => {
          const data = logDoc.data() || {};
          const normalized = normalizeMedicationLogRecord({ id: logDoc.id, ...data });
          if (normalized.category !== 'medication') {
            return;
          }
          if (normalized.status === 'deleted') {
            return;
          }

          nextLogs.push(normalized);

          if (normalized.dateKey !== todayKey) {
            return;
          }

          const key = buildEventKey(
            normalized.medicationId,
            normalized.medicationScheduleId,
            normalized.medicationScheduleIndex,
            normalized.medicationScheduleTime
          ) || normalized.medicationScheduleKey;
          todayMap[key] = normalized;
        });

        if (active) {
          setAllMedicationLogs(nextLogs);
          setLoggedEvents(todayMap);
        }
      } catch (error) {
        console.error('Error loading daily medication logs:', error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadLoggedEvents();
    return () => {
      active = false;
    };
  }, [open, childId]);

  useEffect(() => {
    if (!open) {
      setShowBackfillDialog(false);
      closeDuplicatePrompt();
      setToastState(null);
      setRecentlyMarkedKey(null);
      setDoseEditor(null);
      setEditingLoggedEntry(null);
    }
  }, [open]);

  useEffect(() => {
    if (!doseEditor?.eventKey) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      confirmButtonRef.current?.focus?.();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [doseEditor?.eventKey]);

  useEffect(() => {
    if (!recentlyMarkedKey) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setRecentlyMarkedKey(null);
    }, 240);

    return () => window.clearTimeout(timer);
  }, [recentlyMarkedKey]);

  useEffect(() => {
    if (!toastState) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setToastState(null);
    }, 2800);

    return () => window.clearTimeout(timer);
  }, [toastState]);

  useEffect(() => {
    if (!childId) {
      return undefined;
    }

    setCachedTakenEvents(childId, takenKeys);
    return undefined;
  }, [childId, takenKeys]);

  const scheduledEvents = useMemo(() => {
    const nowMinutes = (() => {
      const now = new Date();
      return now.getHours() * 60 + now.getMinutes();
    })();

    const events = (Array.isArray(resolvedMedications) ? resolvedMedications : [])
      .filter((medication) => !medication.isArchived)
      .flatMap((medication, medicationIndex) => (
        buildMedicationDoseInstancesForDate(
          normalizeMedicationDetail(medication, medication.id || `med-${medicationIndex}`),
          new Date()
        )
      ))
      .map((instance) => {
        const timeMinutes = parseTimeToMinutes(instance.scheduledTime);
        const eventKey = getMedicationLogInstanceKey(instance);
        const persistedLog = findPersistedMedicationLogForInstance(instance, allMedicationLogs);
        const optimisticLog = loggedEvents[eventKey] || null;
        const loggedEvent = persistedLog || optimisticLog;
        const isTaken = Boolean(persistedLog || optimisticLog || takenKeys[eventKey]);
        const dueStatus = !instance.scheduledTime
          ? 'as_needed'
          : (isTaken
              ? 'due'
              : (timeMinutes !== null && timeMinutes < nowMinutes ? 'overdue' : 'upcoming'));

        return {
          ...instance,
          eventKey,
          time: instance.scheduledTime || '',
          timeLabel: formatTimeLabel(instance.scheduledTime),
          scheduleTimeLabel: formatTimeLabel(instance.scheduledTime),
          timeMinutes: timeMinutes ?? Number.MAX_SAFE_INTEGER,
          state: isTaken ? 'taken' : 'due',
          isTaken,
          dueStatus,
          loggedEvent,
          medicationFrequency: instance.scheduleType || (instance.medicationCategory === 'prn' ? 'as_needed' : ''),
        };
      })
      .sort((a, b) => a.timeMinutes - b.timeMinutes || a.medicationName.localeCompare(b.medicationName));

    return events;
  }, [allMedicationLogs, loggedEvents, resolvedMedications, takenKeys]);

  useEffect(() => {
    if (!open || !editEntry?.id || scheduledEvents.length === 0) {
      if (!editEntry?.id) {
        setEditingLoggedEntry(null);
      }
      return;
    }

    const targetEvent = scheduledEvents.find((event) => (
      event.medicationId === (editEntry.medicationId || editEntry.medId)
      && (
        event.scheduleId === editEntry.medicationScheduleId
        || `${event.scheduleIndex}` === `${editEntry.medicationScheduleIndex ?? ''}`
        || event.eventKey === editEntry.medicationScheduleKey
      )
    )) || scheduledEvents.find((event) => event.medicationId === (editEntry.medicationId || editEntry.medId)) || null;

    if (!targetEvent) {
      setEditingLoggedEntry(null);
      return;
    }

    setEditingLoggedEntry(editEntry);
    setDoseEditor({
      eventKey: targetEvent.eventKey,
      dose: parseDoseValue(editEntry.givenDose ?? editEntry.dose ?? targetEvent.dose ?? targetEvent.medicationScheduleDose ?? ''),
      time: editEntry.time || targetEvent.time || new Date().toTimeString().slice(0, 5),
      note: editEntry.note || getEntryContextText(editEntry),
    });
  }, [editEntry, open, scheduledEvents]);

  const medicationDuplicateCandidates = useMemo(() => allMedicationLogs, [allMedicationLogs]);

  const takenCount = scheduledEvents.filter((event) => event.state === 'taken').length;
  const totalCount = scheduledEvents.length;
  const remainingCount = Math.max(0, totalCount - takenCount);
  const allDone = remainingCount === 0 && totalCount > 0;
  const groupedMedicationEvents = useMemo(() => (
    medicationEventGroups
      .map((group) => ({
        ...group,
        events: scheduledEvents.filter((event) => getMedicationEventGroupKey(event) === group.key),
      }))
      .filter((group) => group.events.length > 0)
  ), [scheduledEvents]);

  const buildTakenLogData = (event, editor = {}) => {
    const now = buildTodayTimestampFromTime(editor.time || event.time);
    const prescribedDose = parseDoseNumber(event.plannedDose ?? event.dose ?? event.medicationScheduleDose);
    const givenDose = parseDoseNumber(editor.dose ?? event.plannedDose ?? event.dose ?? event.medicationScheduleDose);
    const note = String(editor.note || '').trim();
    const doseText = [givenDose ?? editor.dose ?? event.plannedDose ?? event.dose, event.unit || 'mg'].filter(Boolean).join(' ').trim();
    const noteSuffix = note ? ` — ${note}` : '';
    const medicationEffect = String(editor.effect || '').trim();
    const medicationEffectLabel = getMedicationEffectLabel(medicationEffect, medicationConfig);
    const medicationVersionSnapshot = event.medicationVersionSnapshot || null;
    const medicationScheduleSnapshot = event.medicationScheduleSnapshot || null;

    const scheduledFor = (() => {
      if (!event.time) {
        return null;
      }

      const [hours, minutes] = event.time.split(':').map((value) => Number(value));
      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return null;
      }

      const scheduled = new Date();
      scheduled.setHours(hours, minutes, 0, 0);
      return scheduled;
    })();

    return {
      childId,
      medId: event.medicationId,
      prescribedDose: prescribedDose ?? null,
      plannedDose: prescribedDose ?? null,
      givenDose: givenDose ?? null,
      actualDose: givenDose ?? null,
      time: editor.time || event.time || '',
      scheduledDate: todayKey,
      scheduledTime: event.time || '',
      note: note || null,
      givenBy: activeUser.uid,
      createdBy: activeUser.uid,
      createdAt: now,
      category: 'medication',
      source: 'medication_schedule_log',
      text: `Gave ${event.medicationName} ${doseText}${noteSuffix}`.trim(),
      timestamp: now,
      timestampUtc: now.toISOString(),
      entryDate: todayKey,
      authorId: activeUser.uid,
      authorName: activeUser.displayName || activeUser.email?.split('@')[0] || 'User',
      authorEmail: activeUser.email,
      medicationId: event.medicationId,
      medicationScheduleId: event.scheduleId,
      medicationScheduleIndex: event.scheduleIndex,
      medicationScheduleKey: event.eventKey,
      medicationScheduleTime: event.time,
      medicationScheduleDose: parseDoseNumber(event.plannedDose || event.dose) ?? (event.plannedDose || event.dose || ''),
      medicationScheduleUnit: event.unit,
      medicationName: event.medicationName,
      medicationCategory: event.medicationCategory || event.category || '',
      medicationFrequency: event.medicationFrequency || '',
      medicationScheduleType: event.scheduleType || '',
      medicationSelectedDays: Array.isArray(event.selectedDays) ? event.selectedDays : [],
      medicationIntervalHours: event.intervalHours || '',
      medicationIntervalStartTime: event.intervalStartTime || '',
      medicationCourseStartDate: event.courseStartDate || '',
      medicationCourseEndDate: event.courseEndDate || '',
      medicationCourseDays: event.courseDays || '',
      medicationCourseDayLabel: event.courseDayLabel || '',
      medicationAsNeededReason: event.asNeededReason || '',
      medicationMaxFrequency: event.maxFrequency || '',
      medicationEffect: medicationEffect || '',
      medicationEffectLabel: medicationEffectLabel || '',
      medicationVersionId: event.medicationVersionId || event.medicationId || '',
      medicationVersionSnapshot,
      medicationScheduleSnapshot,
      scheduledFor: scheduledFor ? scheduledFor.toISOString() : null,
      actualDateTime: now.toISOString(),
      takenAt: now.toISOString(),
      dose: givenDose ?? parseDoseNumber(editor.dose) ?? (event.plannedDose || event.dose || ''),
      unit: event.unit || 'mg',
      status: 'active',
      medicationLogState: 'taken',
    };
  };

  const persistTakenLog = async (event, editor = {}, existingLogId = null) => {
    const loggedDocData = buildTakenLogData(event, editor);
    let docRef = null;
    let savedPayload = loggedDocData;

    if (existingLogId) {
      const { createdAt, ...restLoggedDocData } = loggedDocData;
      const existingLog = editingLoggedEntry?.id === existingLogId
        ? editingLoggedEntry
        : allMedicationLogs.find((log) => log.id === existingLogId) || loggedEvents[event.eventKey] || null;

      savedPayload = {
        ...restLoggedDocData,
        createdAt: existingLog?.createdAt || createdAt,
        updatedBy: activeUser.uid,
        updatedAt: serverTimestamp(),
        status: 'taken',
      };
      docRef = doc(db, 'dailyLogs', existingLogId);
      await updateDoc(docRef, savedPayload);
    } else {
      docRef = await addDoc(collection(db, 'dailyLogs'), loggedDocData);
      savedPayload = {
        ...loggedDocData,
        status: 'taken',
      };
    }

    const savedLog = normalizeMedicationLogRecord({
      id: docRef.id,
      ...savedPayload,
      status: 'taken',
    });
    const nextLoggedEvent = {
      ...savedLog,
      status: 'taken',
    };

    setLoggedEvents((current) => ({
      ...current,
      [event.eventKey]: nextLoggedEvent,
    }));
    setAllMedicationLogs((current) => {
      const next = current.filter((log) => log.id !== docRef.id);
      next.unshift(savedLog);
      return next;
    });
    setTakenKeys((current) => {
      const next = {
        ...current,
        [event.eventKey]: true,
      };
      setCachedTakenEvents(childId, next);
      return next;
    });
    window.dispatchEvent(new CustomEvent(existingLogId ? 'captureez:timeline-refresh' : 'captureez:timeline-entry-created', {
      detail: {
        id: docRef.id,
        collection: 'dailyLogs',
        childId,
        ...savedPayload,
      },
    }));

    return { docRef, loggedDocData };
  };

  const checkMedicationDuplicate = async (event, editor = {}, options = {}) => {
    const referenceTime = buildTodayTimestampFromTime(editor.time || event.time);
    const candidate = normalizeMedicationLogRecord({
      childId,
      category: 'medication',
      source: 'medication_schedule_log',
      status: 'active',
      medicationId: event.medicationId,
      medicationName: event.medicationName,
      medicationScheduleId: event.scheduleId,
      medicationScheduleIndex: event.scheduleIndex,
      medicationScheduleTime: event.time,
      medicationScheduleDose: event.plannedDose || event.dose,
      medicationScheduleUnit: event.unit,
      medicationCategory: event.medicationCategory,
      medicationFrequency: event.medicationFrequency,
      timestamp: referenceTime,
      takenAt: referenceTime,
      scheduledFor: event.time ? referenceTime : null,
    });

    const duplicateInfo = detectPossibleMedicationDuplicate(
      candidate,
      medicationDuplicateCandidates,
      options
    );

    if (!duplicateInfo.matched) {
      return { matched: false, duplicateInfo: null };
    }

    return { matched: true, duplicateInfo };
  };

  const saveMedicationDoseEvent = async (event, editor = {}, { allowDuplicate = false, existingLogId = null } = {}) => {
    if (
      !event
      || !childId
      || !activeUser?.uid
      || (!existingLogId && takenKeys[event.eventKey])
      || savingKey === event.eventKey
    ) {
      return;
    }

    if (!allowDuplicate && !existingLogId) {
      const { matched, duplicateInfo } = await checkMedicationDuplicate(event, editor, {
        windowMinutes: 30,
      });
      if (matched) {
        openDuplicatePrompt({
          reason: `This dose was already logged at ${duplicateInfo.existingLog?.timeLabel || 'this time'}. Log another?`,
          medicationName: event.medicationName,
          existingTimeLabel: duplicateInfo.existingLog?.timeLabel,
          onConfirm: () => {
            closeDuplicatePrompt();
            saveMedicationDoseEvent(event, editor, { allowDuplicate: true, existingLogId });
          },
        });
        return;
      }
    }

    const optimisticLog = {
      id: `optimistic-${event.eventKey}`,
      ...buildTakenLogData(event, editor),
      status: 'taken',
    };

    setSavingKey(event.eventKey);
    setLoggedEvents((current) => ({
      ...current,
      [event.eventKey]: optimisticLog,
    }));
    setTakenKeys((current) => {
      const next = {
        ...current,
        [event.eventKey]: true,
      };
      setCachedTakenEvents(childId, next);
      return next;
    });

    try {
      await persistTakenLog(event, editor, existingLogId);
      setDoseEditor(null);
      setEditingLoggedEntry(null);
      setRecentlyMarkedKey(event.eventKey);
      setToastState({
        event,
        message: medicationConfig.loggedMessage({
          medicationName: event.medicationName,
          doseLabel: getDoseLabel(event),
          timeLabel: formatTimeLabel(editor.time || event.time),
        }),
      });
      onSaved?.({
        eventKey: event.eventKey,
        medicationName: event.medicationName,
        time: formatTimeLabel(editor.time || event.time),
      });
    } catch (error) {
      console.error('Error logging medication dose:', error);
      setLoggedEvents((current) => {
        const next = { ...current };
        delete next[event.eventKey];
        return next;
      });
      setTakenKeys((current) => {
        const next = { ...current };
        delete next[event.eventKey];
        setCachedTakenEvents(childId, next);
        return next;
      });
    } finally {
      setSavingKey(null);
    }
  };

  const handleQuickMarkTaken = async (event) => {
    const plannedDose = parseDoseValue(event.plannedDose ?? event.dose ?? event.medicationScheduleDose ?? '');
    const quickEditor = {
      dose: plannedDose || event.plannedDose || event.dose || event.medicationScheduleDose || '',
      time: new Date().toTimeString().slice(0, 5),
      note: '',
      effect: '',
    };
    if (shouldAskMedicationEffect(event)) {
      handleOpenDoseEditor(event);
      return;
    }
    await saveMedicationDoseEvent(event, quickEditor);
  };

  const handleConfirmDoseEditor = async (eventKey, { allowDuplicate = false } = {}) => {
    const event = scheduledEvents.find((item) => item.eventKey === eventKey);
    const editor = doseEditor?.eventKey === event.eventKey ? doseEditor : null;
    if (!event || !editor) {
      return;
    }

    await saveMedicationDoseEvent(
      event,
      editor,
      {
        allowDuplicate,
        existingLogId: editingLoggedEntry?.id || loggedEvents[event.eventKey]?.id || null,
      }
    );
  };

  const handleUndo = async (event) => {
    const loggedEvent = loggedEvents[event.eventKey];
    if (!loggedEvent?.id || savingKey === event.eventKey) {
      return;
    }

    setToastState(null);
    const previousLog = loggedEvent;
    setSavingKey(event.eventKey);
    setLoggedEvents((current) => {
      const next = { ...current };
      delete next[event.eventKey];
      return next;
    });
    setAllMedicationLogs((current) => current.filter((log) => log.id !== loggedEvent.id));
    setTakenKeys((current) => {
      const next = { ...current };
      delete next[event.eventKey];
      setCachedTakenEvents(childId, next);
      return next;
    });
    try {
      await updateDoc(doc(db, 'dailyLogs', loggedEvent.id), {
        status: 'deleted',
      });
      setToastState({
        event,
        message: 'Returned to pending',
      });
    } catch (error) {
      console.error('Error undoing medication dose:', error);
      setLoggedEvents((current) => ({
        ...current,
        [event.eventKey]: previousLog,
      }));
      setAllMedicationLogs((current) => {
        const next = current.filter((log) => log.id !== loggedEvent.id);
        next.unshift(normalizeMedicationLogRecord(previousLog));
        return next;
      });
      setTakenKeys((current) => {
        const next = {
          ...current,
          [event.eventKey]: true,
        };
        setCachedTakenEvents(childId, next);
        return next;
      });
    } finally {
      setSavingKey(null);
    }
  };

  const handleKeepExistingDuplicate = () => {
    closeDuplicatePrompt();
  };

  const handleLogAgainDuplicate = () => {
    if (duplicatePrompt?.onConfirm) {
      duplicatePrompt.onConfirm();
    }
  };

  const handleToastUndo = async () => {
    if (!toastState?.event) {
      return;
    }

    const event = toastState.event;
    setToastState(null);
    await handleUndo(event);
  };

  return (
    <>
      <LogFormShell
        open={open}
        onClose={onClose}
        title={medicationConfig.title}
        subtitle={allDone
          ? medicationConfig.allDoneSubtitle
          : medicationConfig.remainingSubtitle(remainingCount)}
        titleBadge={childName || undefined}
        compactTitle
        titleSx={{
          fontSize: '20px',
          fontWeight: 700,
          color: medicationSheetColors.ink,
          letterSpacing: '-0.02em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        subtitleSx={{
          marginTop: '8px',
          fontSize: '13.5px',
          color: medicationSheetColors.ink3,
        }}
        titleBadgeSx={{
          bgcolor: medicationSheetColors.meds.bg,
          color: medicationSheetColors.meds.ink,
          height: 26,
          border: 'none',
          fontSize: '11.5px',
          fontWeight: 600,
          '& .MuiChip-label': {
            px: 1.2,
          },
        }}
        closeButtonSx={{
          width: 32,
          height: 32,
          bgcolor: medicationSheetColors.sageMist,
          border: 'none',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: medicationSheetColors.sageSoft,
          },
        }}
        closeIconSx={{
          color: medicationSheetColors.ink2,
          fontSize: 18,
        }}
        mobileBreakpoint="md"
        bodySx={{
          pt: 1.2,
          pb: 1.5,
          px: { xs: 1.1, sm: 2 },
        }}
        surfaceSx={{
          height: 'min(92dvh, 92vh)',
          maxHeight: 'min(92dvh, 92vh)',
        }}
        footer={(
          <Box
            sx={{
              pt: 2,
              mt: 2.2,
              borderTop: `1px solid ${medicationSheetColors.line}`,
            }}
          >
            <Stack direction="row" spacing={1.25}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleManageMedications}
                startIcon={<SettingsRoundedIcon sx={{ fontSize: 18 }} />}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 46,
                  borderRadius: '14px',
                  border: `1px solid ${medicationSheetColors.line}`,
                  color: medicationSheetColors.ink2,
                  bgcolor: '#FFFFFF',
                  fontWeight: 650,
                  fontSize: '13.5px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#FFFFFF',
                    borderColor: medicationSheetColors.meds.soft,
                  },
                }}
              >
                {medicationConfig.manageButtonLabel}
              </Button>
              <Button
                type="button"
                variant="outlined"
                onClick={handleOpenBackfillDialog}
                startIcon={<HistoryRoundedIcon sx={{ fontSize: 18 }} />}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 46,
                  borderRadius: '14px',
                  border: `1px solid ${medicationSheetColors.line}`,
                  color: medicationSheetColors.ink2,
                  bgcolor: '#FFFFFF',
                  fontWeight: 650,
                  fontSize: '13.5px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#FFFFFF',
                    borderColor: medicationSheetColors.meds.soft,
                  },
                }}
              >
                {medicationConfig.backfillButtonLabel}
              </Button>
            </Stack>
          </Box>
        )}
      >
        <Stack spacing={1.25}>
          {(loading || medicationLoading) ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, color: medicationSheetColors.ink3 }}>
                Loading today&apos;s doses…
              </Typography>
            </Box>
          ) : totalCount > 0 ? (
            <>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '18px',
                  p: '14px 16px',
                  bgcolor: 'linear-gradient(135deg, #C9E4D8 0%, #D6E8E1 100%)',
                  background: 'linear-gradient(135deg, #C9E4D8 0%, #D6E8E1 100%)',
                  border: 'none',
                  boxShadow: 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
                  <ProgressRing taken={takenCount} total={totalCount} />

                  <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                    <Typography sx={{ color: medicationSheetColors.meds.ink, fontWeight: 650, fontSize: '14px', lineHeight: 1.15 }}>
                      {medicationConfig.progressLabel(takenCount, totalCount)}
                    </Typography>
                    <Typography sx={{ color: alpha(medicationSheetColors.meds.ink, 0.75), fontWeight: 500, fontSize: '12px', lineHeight: 1.2, mt: 0.35 }}>
                      {medicationConfig.progressRemainingLabel(remainingCount)}
                    </Typography>
                  </Box>

                </Box>
              </Paper>

              <Stack spacing={1.55}>
                {groupedMedicationEvents.map((group) => (
                  <Box key={group.key}>
                    <Typography
                      sx={{
                        mb: 0.75,
                        color: medicationSheetColors.ink2,
                        fontSize: '0.72rem',
                        fontWeight: 900,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {group.label} <Box component="span" sx={{ ml: 0.5 }}>{group.events.length}</Box>
                    </Typography>
                    <Stack spacing={1.25}>
                      {group.events.map((event) => (
                        <MedicationRow
                          key={event.eventKey}
                          event={event}
                          medicationConfig={medicationConfig}
                          onMarkTaken={handleQuickMarkTaken}
                          onLogTime={handleOpenTimeEntry}
                          onUndo={handleUndo}
                          onOpenDoseEditor={handleOpenDoseEditor}
                          onCancelDoseEditor={handleCancelDoseEditor}
                          onConfirmDoseEditor={handleConfirmDoseEditor}
                          onDoseEditorChange={handleDoseEditorChange}
                          doseEditor={doseEditor}
                          confirmButtonRef={confirmButtonRef}
                          savingKey={savingKey}
                          canMarkTaken={Boolean(activeUser?.uid)}
                          recentlyMarkedKey={recentlyMarkedKey}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </>
          ) : (
            <Box
              sx={{
                py: 6,
                px: 2,
                textAlign: 'center',
                borderRadius: 3,
                bgcolor: medicationSheetColors.sageMist,
                border: `1px solid ${medicationSheetColors.line}`,
              }}
            >
              <Typography sx={{ fontWeight: 800, color: medicationSheetColors.ink, mb: 0.5 }}>
                {medicationConfig.emptyTitle}
              </Typography>
              <Typography variant="body2" sx={{ color: medicationSheetColors.ink3 }}>
                {medicationConfig.emptyBody}
              </Typography>
            </Box>
          )}
        </Stack>
      </LogFormShell>

      <MedicationBackfillDialog
        open={showBackfillDialog}
        childId={childId}
        childName={childName}
        medications={resolvedMedications}
        initialMedicationId={timeEntryPreset?.medicationId || ''}
        initialScheduleId={timeEntryPreset?.scheduleId || ''}
        initialStatus={timeEntryPreset?.status || 'taken'}
        initialDate={timeEntryPreset?.initialDate || null}
        initialTime={timeEntryPreset?.initialTime || ''}
        onClose={handleCloseBackfillDialog}
        onSaved={onSaved}
      />

      <MedicationDuplicateWarningDialog
        open={Boolean(duplicatePrompt)}
        reason={duplicatePrompt?.reason || ''}
        medicationName={duplicatePrompt?.medicationName || ''}
        existingTimeLabel={duplicatePrompt?.existingTimeLabel || ''}
        onKeepExisting={handleKeepExistingDuplicate}
        onLogAgainAnyway={handleLogAgainDuplicate}
      />

      <Snackbar
        open={Boolean(toastState)}
        autoHideDuration={2800}
        onClose={() => setToastState(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setToastState(null)}
          action={toastState?.event ? (
            <Button
              color="inherit"
              size="small"
              startIcon={<UndoRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={handleToastUndo}
              sx={{ fontWeight: 700 }}
            >
              Undo log
            </Button>
          ) : null}
          sx={{
            width: '100%',
            bgcolor: medicationSheetColors.meds.ink,
            '& .MuiAlert-message': {
              fontWeight: 650,
            },
          }}
        >
          {toastState?.message || medicationConfig.toastDefault}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BulkMedicationLogDialog;
